import { useMemo } from 'react';
import { QUERY_KEYS, SENTRY_FEATURES } from '@/lib/constants';
import { fetchRecipientCount, previewBroadcast } from '@/lib/api/email-flows';
import { useStanQuery } from '@/queries';
import { combineTags } from '../logic/audienceBuilder.logic';
import type { Fan, Tag } from '../types/audience.types';

interface UseRecipientCountProps {
    includeTags: Tag[];
    excludeTags: Tag[];
    flowId?: number;
    flowEmailId?: number;
}

export const useRecipientCount = ({ includeTags, excludeTags, flowId, flowEmailId }: UseRecipientCountProps) => {
    const queryKey = useMemo(
        () =>
            flowId !== undefined
                ? [QUERY_KEYS.RECIPIENT_COUNT, 'preview', flowId, flowEmailId, includeTags, excludeTags]
                : [QUERY_KEYS.RECIPIENT_COUNT, includeTags, excludeTags],
        [flowId, flowEmailId, includeTags, excludeTags]
    );

    const { data, isLoading, isFetching, error } = useStanQuery({
        queryKey,
        queryFn: async () => {
            const tags = combineTags(includeTags, excludeTags);

            // If no tags, return 0 count immediately
            if (tags.length === 0) {
                return { count: 0, fans: [] };
            }

            if (flowId !== undefined) {
                const result = await previewBroadcast({
                    flow_id: flowId,
                    flow_email_id: flowEmailId,
                    tags,
                });
                // previewBroadcast only returns count, not fans
                return { count: result.count, fans: [] };
            }
            return fetchRecipientCount(tags);
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
        placeholderData: previousData => previousData ?? { count: 0, fans: [] },
        meta: {
            error: {
                sentryMessage: '[EMAIL_FLOWS] Failed to fetch recipient count',
                title: 'Error Loading Recipients',
                message: 'Unable to load recipient count. Please try again.',
                feature: SENTRY_FEATURES.EMAIL_FLOWS,
            },
        },
    });

    return {
        recipientCount: data?.count || 0,
        fans: (data?.fans || []) as Fan[],
        isLoading,
        isFetching,
        error,
    };
};
